import { useState } from "react";
import {
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
  type TextInputProps,
} from "react-native";

import {
  fonts,
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

export function PasswordField({
  accessibilityLabel = "Password",
  invalid = false,
  style,
  ...props
}: TextInputProps & { invalid?: boolean }) {
  const stylesheet = useThemedStyles(makeStyles);
  const [visible, setVisible] = useState(false);

  return (
    <View style={[stylesheet.container, invalid ? stylesheet.invalid : null]}>
      <TextInput
        {...props}
        accessibilityLabel={accessibilityLabel}
        secureTextEntry={!visible}
        style={[stylesheet.input, style]}
      />
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={visible ? "Conceal value" : "Reveal value"}
        onPress={() => setVisible((current) => !current)}
        style={({ hovered, pressed }) => [
          stylesheet.action,
          hovered || pressed ? stylesheet.actionHover : null,
        ]}
      >
        <Text style={stylesheet.actionText}>{visible ? "Hide" : "Show"}</Text>
      </Pressable>
    </View>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      minHeight: 44,
      flexDirection: "row",
      alignItems: "center",
      borderWidth: 1,
      borderColor: colors.borderStrong,
      borderRadius: radii.control,
      backgroundColor: colors.elevated,
    },
    invalid: {
      borderColor: colors.danger,
      backgroundColor: colors.dangerSurface,
    },
    input: {
      flex: 1,
      minHeight: 44,
      paddingHorizontal: spacing.m,
      paddingVertical: spacing.s + 2,
      color: colors.foreground,
      fontFamily: fonts.regular,
      fontSize: 15,
    },
    action: {
      minHeight: 42,
      justifyContent: "center",
      paddingHorizontal: spacing.m,
    },
    actionHover: { backgroundColor: colors.surface },
    actionText: {
      color: colors.mutedForeground,
      fontFamily: fonts.semibold,
      fontSize: 13,
    },
  });
